import {
  AccordionDetailsProps,
  AccordionProps,
  AccordionSummaryProps,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import React from 'react';
import { styled } from '@mui/material/styles';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import AngleBracketIcon from '@/public/icons/angle_bracket.svg';

export const Section = styled(Stack)`
  padding: 0 20px;
`;

export const ButtonSection = styled(Section)`
  margin-top: 16px;
`;

export const DetailSection = styled(Section)`
  margin-top: 32px;
`;

/**
 * 휴가사용 상세내역 테이블 — '근무시간 조회'(OvertimeTable)와 동일한 스타일.
 * 컬럼: 사용일자(col-date) / 분류(col-type) / 수(col-amount) / 케밥(col-kebab)
 * row class: red(전년 초과 사용) / granted(연차·월차 부여) 로 색상 분기.
 */
export const LeaveDetailTable = styled('table')`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  border: 1px solid var(--dk-line, #e7e9ec);
  border-radius: 8px;
  overflow: hidden;

  .col-date {
    width: 100%;
    text-align: left;
  }
  .col-type {
    width: 68px;
    text-align: center;
  }
  .col-amount {
    width: 72px;
    text-align: right;
  }
  .col-kebab {
    width: 36px;
    text-align: center;
    /* 좌우 패딩이 36px 폭을 좁혀 케밥 버튼이 우측으로 밀리므로 가로 패딩 제거 */
    padding: 8px 0;
  }

  thead {
    background: var(--dk-bg-subtle, #f9fafb);
    color: var(--dk-text-secondary, #888d96);

    tr th {
      padding: 8px;
      font-weight: inherit;
      border-bottom: 1px solid var(--dk-line, #e7e9ec);
    }
  }

  tbody {
    tr {
      td {
        padding: 8px;
        color: var(--dk-text, #000);
        font-weight: inherit;
        border-bottom: 1px solid var(--dk-line, #e7e9ec);

        &.col-date {
          color: var(--dk-text-secondary, #888d96);

          &.link {
            text-decoration: underline;
            cursor: pointer;
          }
        }
      }

      &.red td {
        color: var(--dk-danger, #f55060);
      }

      &.granted td.col-date {
        color: var(--dk-text, #000);
      }

      &.granted td.col-type,
      &.granted td.col-amount {
        color: var(--dk-accent, #0066cc);
      }

      &:last-of-type td {
        border-bottom: none;
      }
    }
  }
`;

export const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters {...props} />
))(() => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.MuiAccordion-root': {
    padding: 0,
    border: 'none',
  },
}));

export const AccordionHeader = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<AngleBracketIcon width={'24px'} height={'24px'} />}
    {...props}
  />
))(() => ({
  backgroundColor: 'transparent',
  padding: '16px 16px 16px 20px',
  minHeight: 'auto',
  '&.MuiAccordionSummary-root': {
    margin: 0,
    borderBottom: '1px solid var(--dk-line, #E7E9EC)',
    paddingBottom: '12px',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    color: 'var(--dk-text, #000)',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',

    '& .primary': {
      color: 'var(--dk-accent, #0066CC)',
      fontWeight: 700,
      padding: '0 4px',
    },
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    padding: 0,
  },
}));

export const AccordionBody = styled((props: AccordionDetailsProps) => (
  <MuiAccordionDetails {...props} />
))(() => ({
  '&.MuiAccordionDetails-root': {
    padding: '16px 20px',
  },
}));

export const SummarySubTitle = styled(Typography)`
  font-size: 14px;
  color: var(--dk-text, #000);
`;

export const StyledDivider = styled(Divider)(({ theme }) => ({
  '&:last-of-type': {
    display: 'none', // 마지막 Divider 숨기기
  },
}));
